import { SbsCellStateMap } from "../components/PlanEditor/context/SbsCellStateContext";

import type { SbsStatusType } from "@platform/plan-grid-core";

/** Flat entry shape for mock/API flag data — easily serializable. */
export interface SbsCellStatusEntry {
  planId: string;
  benefitKey: string;
  statuses: SbsStatusType[];
  comment?: string;
  valueState?: "missing" | "null";
}

/**
 * Converts a flat array of status entries into the nested
 * {@link SbsCellStateMap} expected by {@link SbsCellStateProvider}.
 *
 * Duplicate entries for the same (planId, benefitKey) are merged — status are
 * unioned and the last non-undefined comment wins.
 */
export function buildSbsCellStateMap(
  entries: SbsCellStatusEntry[],
): SbsCellStateMap {
  const map: SbsCellStateMap = {};

  for (const entry of entries) {
    const { benefitKey, planId, statuses, comment, valueState } = entry;

    if (!map[benefitKey]) {
      map[benefitKey] = {};
    }

    const existing = map[benefitKey][planId];
    if (existing) {
      // Merge: union flags, keep last comment / valueState
      const mergedFlags = Array.from(
        new Set([...existing.statuses, ...statuses]),
      );
      map[benefitKey][planId] = {
        statuses: mergedFlags,
        comment: comment ?? existing.comment,
        valueState: valueState ?? existing.valueState,
      };
    } else {
      map[benefitKey][planId] = {
        statuses: [...statuses],
        ...(comment !== undefined && { comment }),
        ...(valueState !== undefined && { valueState }),
      };
    }
  }

  return map;
}
